import React from 'react';
import { CanvasDimensions } from './useResponsiveCanvas';

interface PrologueSceneProps {
  dimensions: CanvasDimensions;
}

const SKY_STARS = [
  { x: 0.06, y: 0.12, r: 1.3, op: 0.85 },
  { x: 0.14, y: 0.26, r: 0.9, op: 0.65 },
  { x: 0.21, y: 0.08, r: 1.6, op: 0.9 },
  { x: 0.28, y: 0.22, r: 1.0, op: 0.7 },
  { x: 0.35, y: 0.40, r: 0.8, op: 0.55 },
  { x: 0.42, y: 0.14, r: 1.4, op: 0.85 },
  { x: 0.49, y: 0.29, r: 1.1, op: 0.7 },
  { x: 0.56, y: 0.09, r: 1.7, op: 0.95 },
  { x: 0.63, y: 0.24, r: 0.9, op: 0.6 },
  { x: 0.70, y: 0.15, r: 1.4, op: 0.85 },
  { x: 0.77, y: 0.36, r: 1.0, op: 0.7 },
  { x: 0.84, y: 0.11, r: 1.5, op: 0.9 },
  { x: 0.91, y: 0.28, r: 0.9, op: 0.65 },
  { x: 0.96, y: 0.16, r: 1.2, op: 0.8 },
  { x: 0.04, y: 0.42, r: 1.0, op: 0.6 },
  { x: 0.18, y: 0.50, r: 0.8, op: 0.55 },
  { x: 0.31, y: 0.54, r: 1.2, op: 0.7 },
  { x: 0.66, y: 0.46, r: 1.3, op: 0.8 },
  { x: 0.80, y: 0.50, r: 0.9, op: 0.6 },
  { x: 0.93, y: 0.42, r: 1.1, op: 0.7 },
  { x: 0.11, y: 0.18, r: 0.8, op: 0.5 },
  { x: 0.25, y: 0.34, r: 1.3, op: 0.75 },
  { x: 0.38, y: 0.05, r: 1.2, op: 0.75 },
  { x: 0.52, y: 0.45, r: 0.8, op: 0.5 },
  { x: 0.73, y: 0.06, r: 1.5, op: 0.9 },
  { x: 0.87, y: 0.20, r: 1.0, op: 0.65 },
  { x: 0.02, y: 0.24, r: 1.3, op: 0.8 },
  { x: 0.45, y: 0.21, r: 1.0, op: 0.7 },
  { x: 0.59, y: 0.34, r: 1.2, op: 0.75 },
  { x: 0.85, y: 0.45, r: 0.9, op: 0.6 },
];

export const PrologueAssembly: React.FC<PrologueSceneProps> = React.memo(({ dimensions }) => {
  const { vbWidth, vbHeight, isPortrait } = dimensions;
  const cx = vbWidth / 2;
  const groundY = vbHeight * 0.65;

  // Character & prop offsets adapt to portrait vs widescreen
  const spacingScale = isPortrait ? 0.75 : 1.0;
  const steleX = cx - 210 * spacingScale;
  const brazierX = cx - 90 * spacingScale;
  const aqcicX = cx - 15 * spacingScale;
  const sageX = cx + 175 * spacingScale;

  // Adaptive balustrade arch depth
  const archDepth = isPortrait ? 18 : 32;

  return (
    <svg
      className="w-full h-full absolute inset-0 select-none pointer-events-none"
      viewBox={`0 0 ${vbWidth} ${vbHeight}`}
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Deep Night Atmosphere */}
        <linearGradient id="assemblySky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#020617" />
          <stop offset="45%" stopColor="#091329" />
          <stop offset="85%" stopColor="#111d3d" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Mountain Hazes */}
        <linearGradient id="mtnBackGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0c162c" />
          <stop offset="100%" stopColor="#040814" />
        </linearGradient>
        <linearGradient id="mtnMidGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#121e38" />
          <stop offset="100%" stopColor="#080e1d" />
        </linearGradient>

        {/* Soft Hearth Glow Radiance */}
        <radialGradient id="hearthRadiance" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.75" />
          <stop offset="25%" stopColor="#f59e0b" stopOpacity="0.45" />
          <stop offset="60%" stopColor="#b45309" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </radialGradient>

        {/* Fire Flame Gradient */}
        <linearGradient id="flameColor" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#ef4444" />
          <stop offset="45%" stopColor="#f59e0b" />
          <stop offset="85%" stopColor="#fef08a" />
          <stop offset="100%" stopColor="#ffffff" />
        </linearGradient>

        {/* Terrace Stone Texture */}
        <linearGradient id="pavementStone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="20%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#090f1d" />
        </linearGradient>

        {/* Sage Burnous Shading */}
        <linearGradient id="burnousShade" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="40%" stopColor="#f8fafc" />
          <stop offset="75%" stopColor="#e2e8f0" />
          <stop offset="100%" stopColor="#94a3b8" />
        </linearGradient>

        {/* Subtle Crescent Mask */}
        <mask id="prologueMoonMask">
          <rect x="-40" y="-40" width="80" height="80" fill="#ffffff" />
          <circle cx="9" cy="-5" r="18" fill="#000000" />
        </mask>
      </defs>

      {/* 1. SKY BACKGROUND (Spanning full width) */}
      <rect x="0" y="0" width={vbWidth} height={vbHeight} fill="url(#assemblySky)" />

      {/* Starfield - Natural Uniform Distribution */}
      <g fill="#ffffff">
        {SKY_STARS.map((star, i) => (
          <circle
            key={`star_${i}`}
            cx={star.x * vbWidth}
            cy={star.y * (groundY - 60)}
            r={star.r}
            opacity={star.op}
          />
        ))}
      </g>

      {/* Crescent Moon "Ayyur" in the High Mountain Sky */}
      <g transform={`translate(${isPortrait ? vbWidth - 65 : Math.min(vbWidth - 90, cx + 240)}, ${isPortrait ? 75 : 85})`}>
        <circle cx="0" cy="0" r="32" fill="#fef08a" opacity="0.06" />
        <circle cx="0" cy="0" r="22" fill="#fffbeb" mask="url(#prologueMoonMask)" />
      </g>

      {/* Distant Mountain Silhouettes (Layer 1 - Far Djurdjura Massif - Adaptive sweeping curves) */}
      <path
        d={isPortrait
          ? `M -50 ${groundY - 50} 
             C ${vbWidth * 0.2} ${groundY - 140}, ${vbWidth * 0.4} ${groundY - 165}, ${vbWidth * 0.55} ${groundY - 120} 
             C ${vbWidth * 0.72} ${groundY - 150}, ${vbWidth * 0.88} ${groundY - 110}, ${vbWidth + 50} ${groundY - 75} 
             L ${vbWidth + 50} ${groundY} 
             L -50 ${groundY} Z`
          : `M -100 ${groundY - 60} 
             C ${vbWidth * 0.15} ${groundY - 150}, ${vbWidth * 0.3} ${groundY - 190}, ${vbWidth * 0.45} ${groundY - 130} 
             C ${vbWidth * 0.6} ${groundY - 200}, ${vbWidth * 0.8} ${groundY - 120}, ${vbWidth + 100} ${groundY - 140} 
             L ${vbWidth + 100} ${groundY} 
             L -100 ${groundY} Z`
        }
        fill="url(#mtnBackGrad)"
      />

      {/* Mountain Silhouettes (Layer 2 - Midground Ridge with Distant Village Lights) */}
      <path
        d={isPortrait
          ? `M -50 ${groundY - 20} 
             C ${vbWidth * 0.22} ${groundY - 95}, ${vbWidth * 0.48} ${groundY - 60}, ${vbWidth * 0.72} ${groundY - 100} 
             C ${vbWidth * 0.88} ${groundY - 70}, ${vbWidth * 0.98} ${groundY - 55}, ${vbWidth + 50} ${groundY - 45} 
             L ${vbWidth + 50} ${groundY} 
             L -50 ${groundY} Z`
          : `M -100 ${groundY - 20} 
             C ${vbWidth * 0.2} ${groundY - 110}, ${vbWidth * 0.45} ${groundY - 65}, ${vbWidth * 0.7} ${groundY - 120} 
             C ${vbWidth * 0.88} ${groundY - 60}, ${vbWidth * 0.95} ${groundY - 80}, ${vbWidth + 100} ${groundY - 70} 
             L ${vbWidth + 100} ${groundY} 
             L -100 ${groundY} Z`
        }
        fill="url(#mtnMidGrad)"
      />

      {/* Distant Village Warm Candle Lights on the Ridge */}
      <g fill="#fef08a" opacity="0.8">
        <circle cx={isPortrait ? vbWidth * 0.22 : cx - 180} cy={isPortrait ? groundY - 85 : groundY - 95} r="1.5" />
        <circle cx={isPortrait ? vbWidth * 0.26 : cx - 173} cy={isPortrait ? groundY - 83 : groundY - 93} r="1.2" />
        <circle cx={isPortrait ? vbWidth * 0.29 : cx - 165} cy={isPortrait ? groundY - 86 : groundY - 96} r="1.6" />
        <circle cx={isPortrait ? vbWidth * 0.74 : cx + 190} cy={isPortrait ? groundY - 92 : groundY - 105} r="1.5" />
        <circle cx={isPortrait ? vbWidth * 0.78 : cx + 200} cy={isPortrait ? groundY - 89 : groundY - 102} r="1.3" />
        <circle cx={isPortrait ? vbWidth * 0.83 : cx + 212} cy={isPortrait ? groundY - 94 : groundY - 107} r="1.7" />
      </g>

      {/* 2. TAJMAƐT TERRACE & STONE PAVEMENT */}
      <path
        d={`M -50 ${groundY - 10} Q ${cx} ${groundY - 10 - archDepth} ${vbWidth + 50} ${groundY - 10} L ${vbWidth + 50} ${vbHeight + 50} L -50 ${vbHeight + 50} Z`}
        fill="url(#pavementStone)"
      />

      {/* Flagstone joints in perspective */}
      <path
        d={`M -50 ${groundY + 45} Q ${cx} ${groundY + 45 - archDepth * 0.7} ${vbWidth + 50} ${groundY + 45}`}
        stroke="#1e293b"
        strokeWidth="2.5"
        fill="none"
        opacity="0.6"
      />
      <path
        d={`M -50 ${groundY + 110} Q ${cx} ${groundY + 110 - archDepth * 0.5} ${vbWidth + 50} ${groundY + 110}`}
        stroke="#0f172a"
        strokeWidth="3"
        fill="none"
        opacity="0.8"
      />
      {/* Radial joints */}
      {[-0.6, -0.3, 0, 0.3, 0.6].map((ratio, idx) => (
        <line
          key={`joint_${idx}`}
          x1={cx + ratio * (vbWidth * 0.6)}
          y1={groundY - 10}
          x2={cx + ratio * (vbWidth * 0.9)}
          y2={vbHeight}
          stroke="#0f172a"
          strokeWidth="2"
          opacity="0.5"
        />
      ))}

      {/* Balustrade System: Sturdy Pillars, Base Parapet & Top Handrail (Sans barreaux intermédiaires) */}
      {/* 1. Reinforced Support Pillars (Piliers de soutien réguliers bien ancrés) */}
      <g>
        {Array.from({ length: Math.min(22, Math.max(10, Math.floor(vbWidth / 42))) }).map((_, idx) => {
          const pCount = Math.min(22, Math.max(10, Math.floor(vbWidth / 42)));
          const span = vbWidth + 30;
          const xPos = -15 + (idx * span) / Math.max(1, pCount - 1);
          const x0 = -50;
          const x1 = vbWidth + 50;
          const t = Math.max(0, Math.min(1, (xPos - x0) / (x1 - x0)));
          const curveOffset = 2 * t * (1 - t) * archDepth;
          const topY = groundY - 48 - curveOffset;
          const botY = groundY - 10 - curveOffset + 7;
          return (
            <g key={`pillar_${idx}`}>
              {/* Pillar Shaft */}
              <rect
                x={xPos - 3.5}
                y={topY + 1}
                width="7"
                height={botY - topY - 1}
                rx="1"
                fill="#0f172a"
                stroke="#1e293b"
                strokeWidth="1.2"
              />
              {/* Pillar Top Capital under Handrail */}
              <rect
                x={xPos - 5.5}
                y={topY + 1}
                width="11"
                height="3.5"
                rx="0.5"
                fill="#334155"
                stroke="#1e293b"
                strokeWidth="0.8"
              />
              {/* Pillar Base Plinth into Parapet */}
              <rect
                x={xPos - 5}
                y={botY - 4}
                width="10"
                height="4.5"
                rx="0.5"
                fill="#1e293b"
                stroke="#0f172a"
                strokeWidth="0.8"
              />
              {/* Highlight line */}
              <line
                x1={xPos - 1.5}
                y1={topY + 5}
                x2={xPos - 1.5}
                y2={botY - 4}
                stroke="#475569"
                strokeWidth="1"
                opacity="0.6"
              />
            </g>
          );
        })}
      </g>

      {/* 2. Stone Parapet Balustrade Base (Plinthe solide fixée au sol) */}
      <path
        d={`M -50 ${groundY - 10} Q ${cx} ${groundY - 10 - archDepth} ${vbWidth + 50} ${groundY - 10} L ${vbWidth + 50} ${groundY + 8} Q ${cx} ${groundY + 8 - archDepth} -50 ${groundY + 8} Z`}
        fill="#1e293b"
        stroke="#0f172a"
        strokeWidth="1.5"
      />
      <path
        d={`M -50 ${groundY - 10} Q ${cx} ${groundY - 10 - archDepth} ${vbWidth + 50} ${groundY - 10}`}
        stroke="#334155"
        strokeWidth="1.5"
        fill="none"
      />

      {/* 3. Top Handrail Coping (Balustrade supérieure posée sur les piliers) */}
      <path
        d={`M -50 ${groundY - 48} Q ${cx} ${groundY - 48 - archDepth} ${vbWidth + 50} ${groundY - 48}`}
        stroke="#334155"
        strokeWidth="7"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d={`M -50 ${groundY - 50} Q ${cx} ${groundY - 50 - archDepth} ${vbWidth + 50} ${groundY - 50}`}
        stroke="#64748b"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />

      {/* Hearth Ambient Light Radiance Pool on Terrace */}
      <ellipse
        cx={brazierX}
        cy={groundY + 70}
        rx={190 * spacingScale}
        ry={75 * spacingScale}
        fill="url(#hearthRadiance)"
      />

      {/* 3. ANCESTRAL MONOLITH STELE (Left side of terrace) */}
      <g id="prologue-stele" transform={`translate(${steleX}, ${groundY - 95}) scale(${isPortrait ? 1.05 : 1.25})`}>
        {/* Foundation plinth */}
        <ellipse cx="16" cy="140" rx="30" ry="8" fill="#020617" opacity="0.8" />
        <rect x="0" y="128" width="32" height="14" rx="2" fill="#0f172a" stroke="#1e293b" strokeWidth="2" />
        <rect x="3" y="118" width="26" height="12" rx="2" fill="#1e293b" stroke="#334155" strokeWidth="1.5" />
        {/* Monolith shaft */}
        <polygon points="5,120 7,20 25,20 27,120" fill="#0a0f1d" stroke="#334155" strokeWidth="2" />
        {/* Pyramidion Top */}
        <polygon points="7,20 16,8 25,20" fill="#1e293b" stroke="#334155" strokeWidth="1.5" />
        {/* Chiseled Amazigh geometric lines */}
        <line x1="9" y1="36" x2="23" y2="36" stroke="#334155" strokeWidth="1.2" />
        <line x1="9" y1="102" x2="23" y2="102" stroke="#334155" strokeWidth="1.2" />
        {/* Glowing Sacred Yaz ⵣ */}
        <text
          x="16"
          y="72"
          fill="#38bdf8"
          fontSize="18"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="central"
          filter="drop-shadow(0 0 8px rgba(56,189,248,0.8))"
        >
          ⵣ
        </text>
      </g>

      {/* 4. SACRED HEARTH / BRONZE BRAZIER (Lkanun) */}
      <g id="prologue-brazier" transform={`translate(${brazierX}, ${groundY + 40}) scale(${isPortrait ? 1.1 : 1.3})`}>
        {/* Contact shadow */}
        <ellipse cx="0" cy="30" rx="36" ry="10" fill="#000000" opacity="0.85" />
        {/* Tripod legs */}
        <line x1="-18" y1="12" x2="-24" y2="28" stroke="#0f172a" strokeWidth="4.5" strokeLinecap="round" />
        <line x1="18" y1="12" x2="24" y2="28" stroke="#0f172a" strokeWidth="4.5" strokeLinecap="round" />
        <line x1="0" y1="16" x2="0" y2="30" stroke="#1e293b" strokeWidth="4.5" strokeLinecap="round" />
        {/* Foot pads */}
        <rect x="-28" y="27" width="9" height="4" rx="1.5" fill="#0f172a" />
        <rect x="19" y="27" width="9" height="4" rx="1.5" fill="#0f172a" />
        <rect x="-4.5" y="29" width="9" height="4" rx="1.5" fill="#0f172a" />
        {/* Heavy forged bronze bowl */}
        <path d="M -24 0 Q -26 18 0 18 Q 26 18 24 0 Z" fill="#1c1917" stroke="#451a03" strokeWidth="2.5" />
        <ellipse cx="0" cy="0" rx="24" ry="7" fill="#292524" stroke="#78350f" strokeWidth="1.8" />
        {/* Glowing coals */}
        <ellipse cx="0" cy="1" rx="19" ry="5" fill="#b45309" />
        <ellipse cx="0" cy="1" rx="13" ry="3" fill="#f59e0b" />
        {/* Sacred Living Fire Animation */}
        <path d="M -12 -3 Q -16 -28 0 -42 Q 16 -28 12 -3 Z" fill="url(#flameColor)">
          <animate
            attributeName="d"
            values="M -12 -3 Q -16 -28 0 -42 Q 16 -28 12 -3 Z; M -10 -3 Q -18 -24 0 -46 Q 14 -25 10 -3 Z; M -12 -3 Q -16 -28 0 -42 Q 16 -28 12 -3 Z"
            dur="0.8s"
            repeatCount="indefinite"
          />
        </path>
        <circle cx="0" cy="-18" r="6" fill="#fef08a" opacity="0.9" />
        {/* Floating Sparks */}
        <circle cx="-4" cy="-45" r="1.6" fill="#fef08a">
          <animate attributeName="cy" values="-35; -70" dur="1.2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1; 0" dur="1.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="6" cy="-40" r="1.8" fill="#f97316">
          <animate attributeName="cy" values="-30; -65" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1; 0" dur="1.5s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* 5. AQCIC (The Young Hero) Listening Attentively near the Hearth */}
      <g id="prologue-aqcic" transform={`translate(${aqcicX}, ${groundY - 15}) scale(${isPortrait ? 2.3 : 2.6})`}>
        {/* Ground shadow */}
        <ellipse cx="14" cy="40" rx="14" ry="4" fill="#000000" opacity="0.7" />
        {/* Walking staff resting near him */}
        <line x1="26" y1="10" x2="26" y2="40" stroke="#78350f" strokeWidth="2" strokeLinecap="round" />
        <circle cx="26" cy="9" r="2.5" fill="#f59e0b" stroke="#78350f" strokeWidth="0.5" />
        {/* Legs & Boots */}
        <rect x="9" y="28" width="4" height="9" fill="#334155" />
        <rect x="15" y="28" width="4" height="9" fill="#334155" />
        <rect x="8" y="37" width="5" height="4" fill="#1e293b" />
        <rect x="15" y="37" width="5" height="4" fill="#1e293b" />
        {/* Torso: Iconic Red Jacket */}
        <rect x="7" y="18" width="14" height="11" fill="#dc2626" rx="1" />
        <rect x="12" y="18" width="4" height="11" fill="#f8fafc" />
        {/* Arms */}
        <rect x="4" y="19" width="3.5" height="7" fill="#b91c1c" />
        <rect x="3.5" y="24" width="4" height="4" fill="#fcd34d" />
        <rect x="21" y="19" width="3.5" height="7" fill="#b91c1c" />
        <rect x="20.5" y="24" width="4" height="4" fill="#fcd34d" />
        {/* Face */}
        <rect x="8" y="10" width="12" height="9" fill="#fcd34d" rx="1" />
        {/* Two dark pixel eyes attentive */}
        <rect x="11" y="13" width="2" height="2" fill="#000000" />
        <rect x="16" y="13" width="2" height="2" fill="#000000" />
        {/* Fedora Hat */}
        <rect x="6" y="4" width="16" height="6" fill="#1e293b" rx="1" />
        <rect x="4" y="8" width="20" height="3" fill="#1e293b" rx="0.5" />
        {/* Warm hearth rim light highlight on hero's left side */}
        <rect x="6" y="5" width="1.5" height="6" fill="#fbbf24" opacity="0.4" />
        <rect x="4" y="9" width="2" height="2" fill="#fbbf24" opacity="0.5" />
        <rect x="7" y="19" width="1.5" height="10" fill="#fbbf24" opacity="0.4" />
      </g>

      {/* 6. AMUSNAW (LE SAGE) — Dignified Village Elder Holding Staff & Welcoming */}
      <g id="prologue-sage" transform={`translate(${sageX}, ${groundY - 55}) scale(${isPortrait ? 1.4 : 1.65})`}>
        {/* Contact shadow */}
        <ellipse cx="32" cy="115" rx="34" ry="10" fill="#000000" opacity="0.65" />

        {/* Traditional Shepherd's Staff (Aɛkkaz) with top pommel */}
        <line x1="62" y1="5" x2="62" y2="116" stroke="#78350f" strokeWidth="4" strokeLinecap="round" />
        <circle cx="62" cy="5" r="4.5" fill="#f59e0b" stroke="#78350f" strokeWidth="1" />
        <circle cx="63.5" cy="4" r="1.5" fill="#fef08a" />

        {/* Flowing White Burnous */}
        <path
          d="M 10 114 L 14 34 L 52 34 L 56 114 Z"
          fill="url(#burnousShade)"
          stroke="#cbd5e1"
          strokeWidth="1.5"
        />
        {/* Robe pleat folds */}
        <path d="M 26 36 L 30 114" stroke="#94a3b8" strokeWidth="2" opacity="0.5" />
        <path d="M 42 38 L 45 114" stroke="#cbd5e1" strokeWidth="1.5" opacity="0.7" />
        {/* Berber Golden Triangular Collar Trim (Asaru) & Ancestral Neck Jewel */}
        <path d="M 16 35 L 30 52 L 44 35" stroke="#ca8a04" strokeWidth="2.5" fill="none" />
        <path d="M 20 36 Q 30 50 40 36" stroke="#cbd5e1" strokeWidth="1.5" fill="none" />
        <polygon points="30,40 36,50 24,50" fill="#fbbf24" stroke="#b45309" strokeWidth="1" />
        <circle cx="30" cy="46" r="2.5" fill="#dc2626" stroke="#7f1d1d" strokeWidth="0.5" />
        <circle cx="30" cy="51" r="1.5" fill="#38bdf8" />
        {/* Hood & Cheche */}
        <circle cx="30" cy="22" r="16" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.2" />
        <ellipse cx="30" cy="20" rx="13" ry="11" fill="#e2e8f0" />
        {/* Face */}
        <circle cx="25" cy="23" r="7.5" fill="#d97706" />
        <ellipse cx="23" cy="22" rx="1.5" ry="1.2" fill="#0f172a" />
        <path d="M 21 19 Q 24 18 27 20" stroke="#78350f" strokeWidth="1" fill="none" />
        {/* White flowing beard */}
        <path d="M 18 27 Q 27 50 36 27 Z" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1" />

        {/* Right Arm holding the staff (Aɛkkaz) */}
        <path
          d="M 46 40 L 60 45 L 57 55 L 43 51 Z"
          fill="url(#burnousShade)"
          stroke="#cbd5e1"
          strokeWidth="1.2"
        />
        {/* Hand gripping the staff */}
        <ellipse cx="62" cy="48" rx="4.5" ry="3.5" fill="#d97706" />
        <line x1="60" y1="48" x2="64" y2="48" stroke="#b45309" strokeWidth="0.8" />

        {/* Welcoming Left Arm reaching toward Aqcic and the hearth */}
        <path
          d="M 14 42 L -6 50 L -24 48 L -20 58 Q 6 60 18 52 Z"
          fill="url(#burnousShade)"
          stroke="#cbd5e1"
          strokeWidth="1.2"
        />
        <circle cx="-24" cy="48" r="4.5" fill="#d97706" />
      </g>

      {/* 7. ASSEMBLED VILLAGE ELDERS (Council members in the background) */}
      {!isPortrait && (
        <g id="prologue-villagers" opacity="0.9">
          {/* Elder sitting left of stele */}
          <g transform={`translate(${steleX - 90}, ${groundY - 10}) scale(1.3)`}>
            <ellipse cx="14" cy="40" rx="16" ry="5" fill="#020617" opacity="0.75" />
            <path d="M 3 38 L 7 14 L 21 14 L 25 38 Z" fill="#09101d" stroke="#050a12" strokeWidth="0.8" />
            <circle cx="14" cy="8" r="6" fill="#0c1626" />
            <ellipse cx="14" cy="6" rx="6" ry="3.5" fill="#111d33" />
          </g>
          {/* Elder sitting right of sage with iron lantern */}
          <g transform={`translate(${sageX + 130}, ${groundY - 5}) scale(1.3)`}>
            <ellipse cx="14" cy="40" rx="16" ry="5" fill="#020617" opacity="0.75" />
            <path d="M 3 38 L 7 14 L 21 14 L 25 38 Z" fill="#09101d" stroke="#050a12" strokeWidth="0.8" />
            <circle cx="14" cy="8" r="6" fill="#0c1626" />
            {/* Hanging lantern */}
            <line x1="24" y1="20" x2="28" y2="28" stroke="#1c1917" strokeWidth="1.5" />
            <rect x="25" y="28" width="6" height="8" rx="1" fill="#b45309" stroke="#451a03" strokeWidth="1" />
            <circle cx="28" cy="32" r="2.5" fill="#fef08a" />
            <circle cx="28" cy="32" r="8" fill="#fbbf24" opacity="0.2" />
          </g>
        </g>
      )}

      {/* 8. FOREGROUND CINEMATIC FRAMING: Olive Tree Branch (Azemmur) */}
      <g transform={isPortrait ? 'translate(-10, -10) scale(0.85)' : 'translate(0, 0)'}>
        <path d="M -20 -10 Q 80 20 140 85" stroke="#1c1917" strokeWidth="7" strokeLinecap="round" fill="none" />
        <path d="M 60 18 Q 110 12 130 50" stroke="#1c1917" strokeWidth="4" strokeLinecap="round" fill="none" />
        {/* Lush olive leaves */}
        <ellipse cx="90" cy="35" rx="13" ry="5" transform="rotate(25 90 35)" fill="#15803d" />
        <ellipse cx="120" cy="48" rx="12" ry="5" transform="rotate(-15 120 48)" fill="#166534" />
        <ellipse cx="138" cy="85" rx="14" ry="5.5" transform="rotate(40 138 85)" fill="#15803d" />
        <ellipse cx="75" cy="18" rx="10" ry="4" transform="rotate(-30 75 18)" fill="#22c55e" opacity="0.85" />
        {/* Ripe olives */}
        <ellipse cx="98" cy="44" rx="3.5" ry="5" fill="#0f172a" stroke="#1e293b" strokeWidth="0.8" />
        <ellipse cx="125" cy="60" rx="3.5" ry="5" fill="#0f172a" stroke="#1e293b" strokeWidth="0.8" />
      </g>
    </svg>
  );
});
